import { api } from "../config/AxiosConfig";
import { API_ROOT } from "./ApiUrlFormatter";
import {
    DashboardFilterValue,
    BreakpointLayouts,
    DashboardLayoutConfig,
    DashboardPayloadResource,
    GridLayout,
    GridItemResource,
    SaveDashboardLayoutRequest,
    UpdateLayoutsRequest,
    UpdateWidgetSettingsRequest,
    WidgetSettings,
} from "../../models/Dashboard";
import {
    extractFilterRows,
    normalizeGlobalFilters,
    toDashboardFilterRowsPayload,
} from "../../utils/DashboardFilters";
import { ActivationAndLoadsWidgetModel } from "../../models/ActivationAndLoadsWidgetModel";
import { MerchantTypeWidgetModel } from "../../models/MerchantTypeWidgetModel";
import { MerchantWidgetModel } from "../../models/MerchantWidgetModel";
import { SettlementWidgetModel, TransactionTypeCountAmountModel } from "../../models/SettlementWidgetModel";

const mapResourceToGridLayout = (item: GridItemResource): GridLayout => ({
    i: item.widgetId,
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
    minW: item.minW,
    minH: item.minH,
    maxW: item.maxW,
    maxH: item.maxH,
    static: item.static,
});

const mapGridLayoutToModel = (layout: GridLayout): GridLayout => ({
    i: layout.i,
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
    minW: layout.minW,
    minH: layout.minH,
    maxW: layout.maxW,
    maxH: layout.maxH,
    static: layout.static,
});

const mapBreakpointsToModel = (bp: BreakpointLayouts): BreakpointLayouts => {
    const toModels = (items?: GridLayout[]) => items?.map((l) => mapGridLayoutToModel(l));
    return {
        lg: toModels(bp.lg),
        md: toModels(bp.md),
        sm: toModels(bp.sm),
        xs: toModels(bp.xs),
        xxs: toModels(bp.xxs),
    };
};

const mapPayloadToConfig = (payload: DashboardPayloadResource): DashboardLayoutConfig => {
    const mapItems = (items?: GridItemResource[]) => (items ?? []).map((r) => mapResourceToGridLayout(r));

    const layouts: BreakpointLayouts = payload.layouts
        ? {
              lg: mapItems(payload.layouts.lg),
              md: mapItems(payload.layouts.md),
              sm: mapItems(payload.layouts.sm),
              xs: mapItems(payload.layouts.xs),
              xxs: mapItems(payload.layouts.xxs),
          }
        : { lg: mapItems(payload.layout) };

    return {
        layoutId: payload.layoutId,
        layoutKey: payload.layoutKey,
        layoutName: payload.layoutName || "Default Dashboard",
        isDefault: payload.isDefault,
        rowHeight: payload.rowHeight,
        cols: payload.cols,
        layouts,
        widgets: (payload.widgets ?? []).map((w) => ({
            widgetId: w.widgetId,
            definitionId: w.definitionId,
            displayName: w.displayName,
            isVisible: w.isVisible,
            sortOrder: w.sortOrder,
            settings: (w.settings ?? {}) as WidgetSettings,
        })),
        globalFilters: normalizeGlobalFilters(payload.globalFilters),
    };
};

const mapGlobalFiltersToRows = (
    filters: unknown
): DashboardFilterValue[] => {
    const normalized = normalizeGlobalFilters(filters);
    return extractFilterRows(normalized);
};

const mapConfigToSaveRequest = (layout: DashboardLayoutConfig): SaveDashboardLayoutRequest => ({
    layoutKey: layout.layoutKey,
    layoutName: layout.layoutName || "Default Dashboard",
    isDefault: layout.isDefault,
    rowHeight: layout.rowHeight,
    layouts: mapBreakpointsToModel(layout.layouts),
    widgets: layout.widgets.map((w) => ({
        widgetId: w.widgetId,
        definitionId: w.definitionId,
        displayName: w.displayName,
        isVisible: w.isVisible ?? true,
        sortOrder: w.sortOrder,
        settings: w.settings as Record<string, unknown>,
    })),
    globalFilters: {
        dashboardFilters: toDashboardFilterRowsPayload(
            mapGlobalFiltersToRows(layout.globalFilters)
        ),
    },
});

export const DashboardApi = {
    getLayout: async (layoutKey: string): Promise<DashboardPayloadResource | null> => {
        const response = await api.get<DashboardPayloadResource>(`${API_ROOT}/reporthub/dashboard/${layoutKey}`);
        return response.data;
    },

    loadLayoutConfig: async (layoutKey: string): Promise<DashboardLayoutConfig | null> => {
        try {
            const payload = await DashboardApi.getLayout(layoutKey);
            if (!payload) return null;
            return mapPayloadToConfig(payload);
        } catch (error) {
            console.error("Failed to load layout:", error);
            return null;
        }
    },

    saveLayout: async (layoutKey: string, request: SaveDashboardLayoutRequest): Promise<number> => {
        const response = await api.post<number>(`${API_ROOT}/reporthub/dashboard/${layoutKey}/save`, request);
        return response.data;
    },

    saveLayoutConfig: async (layout: DashboardLayoutConfig): Promise<number> => {
        return DashboardApi.saveLayout(layout.layoutKey, mapConfigToSaveRequest(layout));
    },

    updateWidgetSettings: async (
        layoutKey: string,
        widgetId: string,
        request: UpdateWidgetSettingsRequest
    ): Promise<number> => {
        const response = await api.patch<number>(
            `${API_ROOT}/reporthub/dashboard/${layoutKey}/widget/${widgetId}/settings`,
            request
        );
        return response.data;
    },

    saveWidgetSettings: async (
        layoutKey: string,
        widgetId: string,
        settings: WidgetSettings
    ): Promise<number> => {
        return DashboardApi.updateWidgetSettings(layoutKey, widgetId, {
            settings: settings as Record<string, unknown>,
        });
    },

    updateLayouts: async (layoutKey: string, request: UpdateLayoutsRequest): Promise<number> => {
        const response = await api.patch<number>(`${API_ROOT}/reporthub/dashboard/${layoutKey}/layouts`, request);
        return response.data;
    },

    saveLayouts: async (layoutKey: string, layouts: BreakpointLayouts): Promise<number> => {
        return DashboardApi.updateLayouts(layoutKey, {
            layouts: mapBreakpointsToModel(layouts),
        });
    },

    saveGlobalFilters: async (layoutKey: string, filters: unknown): Promise<void> => {
        const payload = {
            globalFilters: normalizeGlobalFilters(filters),
        };

        await api.post<void>(`${API_ROOT}/reporthub/dashboard/${layoutKey}/global-filters`, payload);
    },

    clearGlobalFilters: async (layoutKey: string): Promise<void> => {
        await api.delete<void>(`${API_ROOT}/reporthub/dashboard/${layoutKey}/global-filters`);
    },

    deleteWidget: async (_layoutKey: string, _widgetId: string): Promise<void> => {
        return Promise.resolve();
    },

    activationAndLoadsWidgetData: async (
        developer: string,
        program?: string,
        merchantGroup?: string,
        date?: string,
        dateRange: number = 1
    ): Promise<ActivationAndLoadsWidgetModel> => {
        if (!developer) {
            throw new Error("developer is required");
        }

        const params: Record<string, string> = { developer };
        if (program) params.program = program;
        if (merchantGroup) params.merchantGroup = merchantGroup;
        if (date) params.date = date;
        if (dateRange) params.dateRange = dateRange.toString();

        try {
            const response = await api.get<ActivationAndLoadsWidgetModel>(
                `${API_ROOT}/reporthub/dashboard/widgets/activationandloads`, 
                { params }
            );

            const raw = response.data;
            const counts = (raw?.transactionCounts ?? []) as any[];

            const transactionCounts = counts.map((c) => ({
                systemTransactionTypeId: c.systemTransactionTypeId,
                transactionCount: c.transactionCount,
            }));

            return { transactionCounts };
        } catch (error) {
            console.error("Failed to fetch activations and loads widget data:", error);
            throw error;
        }
    },

    mccWidgetData: async (
        developer: string,
        program?: string,
        merchantGroup?: string,
        date?: string,
        count: number = 10,
        dateRange: number = 1
    ): Promise<MerchantTypeWidgetModel> => {
        if (!developer) {
            throw new Error("developer is required");
        }

        const params: Record<string, string> = { developer };
        if (program) params.program = program;
        if (merchantGroup) params.merchantGroup = merchantGroup;
        if (date) params.date = date;
        if (count) params.count = count.toString();
        if (dateRange) params.dateRange = dateRange.toString();

        try {
            const response = await api.get(
                `${API_ROOT}/reporthub/dashboard/widgets/mccs`, 
                { params }
            );

            const raw = response.data;
            const counts = (raw?.transactionCounts ?? []) as any[];

            const transactionCounts = counts.map((c) => ({
                merchantTypeCode: c.merchantTypeCode,
                transactionCount: c.transactionCount,
            }));

            return { transactionCounts };
        } catch (error) {
            console.error("Failed to fetch MCC widget data:", error);
            throw error;
        }
    },

    merchantWidgetData: async (
        developer: string,
        program?: string,
        merchantGroup?: string,
        date?: string,
        count: number = 10,
        dateRange: number = 1
    ): Promise<MerchantWidgetModel> => {
        if (!developer) {
            throw new Error("developer is required");
        }

        const params: Record<string, string> = { developer };
        if (program) params.program = program;
        if (merchantGroup) params.merchantGroup = merchantGroup;
        if (date) params.date = date;
        if (count) params.count = count.toString();
        if (dateRange) params.dateRange = dateRange.toString();

        try {
            const response = await api.get<MerchantWidgetModel>(
                `${API_ROOT}/reporthub/dashboard/widgets/merchants`, 
                { params }
            );

            const raw = response.data;
            const counts = (raw?.transactionCounts ?? []) as any[];

            const transactionCounts = counts.map((c) => ({
                merchant: c.merchant,
                transactionCount: c.transactionCount,
            }));

            return { transactionCounts };
        } catch (error) {
            console.error("Failed to fetch merchant widget data:", error);
            throw error;
        }

    },

    settlementWidgetData: async (
        developer: string,
        program?: string,
        merchantGroup?: string,
        date?: string,
        dateRange: number = 1
    ): Promise<SettlementWidgetModel> => {
        if (!developer) {
            throw new Error("developer is required");
        }

        const params: Record<string, string> = { developer };
        if (program) params.program = program;
        if (merchantGroup) params.merchantGroup = merchantGroup;
        if (date) params.date = date;
        if (dateRange) params.dateRange = dateRange.toString();

        try {
            const response = await api.get(
                `${API_ROOT}/reporthub/dashboard/widgets/settlement`, 
                { params }
            );

            const raw = response.data;
            const targetDayPurchases = (raw?.targetDayPurchases ?? []) as TransactionTypeCountAmountModel[];
            const targetDayPurchaseReturns = (raw?.targetDayPurchaseReturns ?? []) as TransactionTypeCountAmountModel[];
            const previousDayPurchases = (raw?.previousDayPurchases ?? []) as TransactionTypeCountAmountModel[];
            const previousDayPurchaseReturns = (raw?.previousDayPurchaseReturns ?? []) as TransactionTypeCountAmountModel[];

            return { targetDayPurchases, targetDayPurchaseReturns, previousDayPurchases, previousDayPurchaseReturns };
        } catch (error) {
            console.error("Failed to fetch settlement widget data:", error);
            throw error;
        }
    }
};